import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, LineChart } from 'lucide-react';
import { ChartModalWrapper } from '@src/features/dashboard/components/chart-modal-wrapper';
import { ChartTooltipContent } from '@src/features/dashboard/components/chart-tooltip-content';
import { Card, CardContent, CardHeader } from '@src/design-system/card';
import { Text } from '@src/design-system/text';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/design-system/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { Checkbox } from '@src/design-system/checkbox';
import { cn } from '@src/design-system/helpers';
import { ChartContainer, ChartTooltip } from '@src/design-system/chart';
import { Bar, BarChart, CartesianGrid, Legend, XAxis } from 'recharts';
import SkeletonLoader from '@src/shared/components/reusables/skeleton';
import { REMINDER_CHANNELS } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { Activity, Calendar } from 'iconsax-react';
import reminderApi from '@src/features/reminders/services/reminder-api';
import { DefaultDateVlue } from '@src/features/dashboard/constants';
import { getDateRangeObject } from '@src/features/dashboard/helper/date-helper';
import { applyChannelMaskToPerformance } from '../../../utils/performance-data';

const PERFORMANCE_YEAR_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  String(new Date().getFullYear() - i)
);

export function ReminderOverviewPerformance() {
  const [params] = useSearchParams();
  const selectedChannels = useMemo(
    () => params.getAll('channel'),
    [params.toString()]
  );
  const selectedPrograms = useMemo(
    () => params.getAll('programs[]'),
    [params.toString()]
  );
  const [performanceChannels, setPerformanceChannels] = useState<string[]>([]);
  const [performanceYear, setPerformanceYear] = useState<string>('');
  const hasPerformanceYearParam = Boolean(performanceYear);
  const isAllPerformanceChannelsSelected = performanceChannels.length === 0;
  const dateFilter = params.get('date') || DefaultDateVlue;

  const dashboardQueryParams = useMemo(() => {
    const range = getDateRangeObject(dateFilter);
    const out: Record<string, string | string[]> = {};
    if (range) {
      out.start_date = range.start_date;
      out.end_date = range.end_date;
    }
    if (selectedChannels.length > 0) out.channel = selectedChannels;
    if (selectedPrograms.length > 0) out.programs = selectedPrograms;
    return out;
  }, [dateFilter, selectedChannels, selectedPrograms]);

  const performanceQueryParams = useMemo(() => {
    const out: Record<string, string | string[]> = {
      ...dashboardQueryParams,
    };

    if (hasPerformanceYearParam) {
      const y = parseInt(performanceYear, 10);
      const safeY = Number.isNaN(y) ? new Date().getFullYear() : y;
      const start = `${safeY}-01-01`;
      const today = new Date().toISOString().slice(0, 10);
      const end = safeY === new Date().getFullYear() ? today : `${safeY}-12-31`;
      out.start_date = start;
      out.end_date = end;
    }

    if (performanceChannels.length > 0 && selectedChannels.length > 0) {
      out.channel = performanceChannels.filter((ch) =>
        selectedChannels.includes(ch)
      );
    } else if (performanceChannels.length > 0) {
      out.channel = performanceChannels;
    } else if (selectedChannels.length > 0) {
      out.channel = selectedChannels;
    }

    return out;
  }, [
    performanceYear,
    performanceChannels,
    selectedChannels,
    dashboardQueryParams,
    hasPerformanceYearParam,
  ]);

  const { data: performanceData, isLoading } =
    reminderApi.useGetReminderPerformance(performanceQueryParams);

  const data = useMemo(
    () =>
      applyChannelMaskToPerformance(
        performanceData?.data,
        performanceChannels.length > 0 ? performanceChannels : selectedChannels
      ),
    [performanceData?.data, performanceChannels, selectedChannels]
  );

  const onPerformanceChannelsChange = (channels: string[]) => {
    setPerformanceChannels(channels);
  };

  const onPerformanceYearChange = (year: string) => {
    setPerformanceYear(year);
  };

  // If any outer/global filter changes, clear local performance refinements.
  useEffect(() => {
    setPerformanceChannels([]);
    setPerformanceYear('');
  }, [dateFilter, selectedChannels, selectedPrograms]);

  return (
    <ChartModalWrapper>
      <Card className='flex max-h-[430px] min-h-[430px] w-[100%] flex-col rounded-2xl border border-primary-100 bg-white pb-4 shadow-sm'>
        <CardHeader className='p-2 bg-primary-50 m-3 rounded-[8px] '>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex items-center gap-x-3'>
              <LineChart className='size-6 shrink-0' />
              <Text className='text-xl font-semibold text-gray-1000'>
                Performance
              </Text>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <Popover>
                <PopoverTrigger
                  className={cn(
                    'flex h-10 w-fit min-w-[140px] cursor-pointer items-center gap-2 rounded-xl border border-primary-200 bg-white px-3 text-left text-gray-1000 hover:bg-primary-50'
                  )}
                >
                  <Activity className='size-5 shrink-0 text-secondary-1000' />
                  <span className='truncate text-sm font-medium'>
                    {isAllPerformanceChannelsSelected
                      ? 'All channels'
                      : `${performanceChannels.length} channel${performanceChannels.length === 1 ? '' : 's'}`}
                  </span>
                  <ChevronDown
                    className='size-4 shrink-0'
                    aria-hidden
                  />
                </PopoverTrigger>
                <PopoverContent
                  className='z-50 w-[170px] rounded-lg border border-primary-200 bg-white p-2 shadow-md'
                  align='end'
                >
                  <div className='flex flex-col gap-1'>
                    {Object.values(REMINDER_CHANNELS).map((ch) => (
                      <label
                        key={ch}
                        className='flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-primary-50'
                      >
                        <Checkbox
                          checked={performanceChannels.includes(ch)}
                          onCheckedChange={(checked) => {
                            if (checked === true) {
                              if (isAllPerformanceChannelsSelected) {
                                onPerformanceChannelsChange([ch]);
                              } else if (!performanceChannels.includes(ch)) {
                                onPerformanceChannelsChange([
                                  ...performanceChannels,
                                  ch,
                                ]);
                              }
                            } else if (
                              checked === false &&
                              !isAllPerformanceChannelsSelected
                            ) {
                              onPerformanceChannelsChange(
                                performanceChannels.filter(
                                  (c: string) => c !== ch
                                )
                              );
                            }
                          }}
                          className='border border-primary-200'
                        />
                        <span className='text-sm text-gray-1000'>
                          {formatTextForDisplay(ch)}
                        </span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Select
                value={performanceYear}
                onValueChange={onPerformanceYearChange}
              >
                <SelectTrigger className='h-10 w-fit min-w-[100px] cursor-pointer gap-2 rounded-xl border border-primary-200 bg-white px-3 text-gray-1000 hover:bg-primary-50 data-[placeholder]:font-medium data-[placeholder]:text-gray-1000'>
                  <Calendar className='size-5 shrink-0 text-secondary-1000' />
                  <SelectValue
                    placeholder='Year'
                    className='truncate text-sm font-medium text-gray-1000'
                  />
                </SelectTrigger>
                <SelectContent>
                  {PERFORMANCE_YEAR_OPTIONS.map((y) => (
                    <SelectItem
                      key={y}
                      value={y}
                    >
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className='mb-4 flex flex-col pl-0'>
          {isLoading ? (
            <SkeletonLoader height={250} />
          ) : (
            <ChartContainer
              config={{}}
              className='aspect-square w-full max-h-[250px]'
            >
              <BarChart
                data={data}
                barSize={14}
              >
                <XAxis
                  dataKey='month'
                  tickLine
                  tickMargin={10}
                  axisLine={false}
                />
                <CartesianGrid
                  vertical={false}
                  horizontal={false}
                />
                <Bar
                  dataKey='whatsapp'
                  stackId='a'
                  fill='#22C3A0'
                  name='What’s App'
                />
                <Bar
                  dataKey='email'
                  stackId='a'
                  fill='#DE483B'
                  name='Email'
                />
                <Bar
                  dataKey='sms'
                  stackId='a'
                  fill='#3B82F6'
                  name='SMS'
                  radius={[2, 2, 0, 0]}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  cursor={false}
                />
                <Legend
                  iconType='circle'
                  verticalAlign='bottom'
                  align='left'
                  wrapperStyle={{
                    marginTop: 40,
                    marginLeft: 24,
                    bottom: -45,
                  }}
                  formatter={(value) => (
                    <span
                      style={{
                        fontSize: 14,
                        marginRight: 30,
                        fontWeight: 500,
                        color: '#000',
                        textTransform: 'capitalize',
                      }}
                    >
                      {value}
                    </span>
                  )}
                />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
    </ChartModalWrapper>
  );
}
